import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamily } from '../contexts/FamilyContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import FeedIcon from '@mui/icons-material/Feed';
import GroupIcon from '@mui/icons-material/Group';
import NewsFeedCarousel from '../components/NewsFeedCarousel';

const Home = () => {
  const navigate = useNavigate();
  const { currentFamily, families, selectFamily, loading } = useFamily();
  const { user } = useAuth();
  
  // Get icon color from family settings, or use theme default
  const iconColor = currentFamily?.icon_color || '#B8956A';

  useEffect(() => {
    // Redirect to families page if no family and user is logged in
    if (user && families.length === 0) {
      // Don't redirect automatically, just show message
    }
  }, [user, families]);

  const getFeatures = () => [
    {
      title: 'Celebrations',
      description: 'Create and manage celebrations with food signups, RSVP, photos, and chat',
      icon: <EventIcon sx={{ fontSize: 60 }} />,
      path: '/events',
      color: '#D4A574', // Warm gold
    },
    {
      title: 'Gift Wishlists',
      description: 'Share your wishlist and see what others want',
      icon: <CardGiftcardIcon sx={{ fontSize: 60 }} />,
      path: '/wishlists',
      color: '#C9A961', // Champagne gold
    },
    {
      title: 'Name Picker',
      description: 'Virtual name drawing for Secret Santa and gift exchanges',
      icon: <ShuffleIcon sx={{ fontSize: 60 }} />,
      path: '/name-picker',
      color: '#B8956A', // Medium gold
    },
    {
      title: 'Recipes',
      description: 'Share and discover recipes',
      icon: <CardGiftcardIcon sx={{ fontSize: 60 }} />,
      path: '/recipes',
      color: '#8B7D6B', // Neutral brown
    },
    {
      title: 'Gift Tracker',
      description: 'Track who gave what to whom',
      icon: <CardGiftcardIcon sx={{ fontSize: 60 }} />,
      path: '/gift-tracker',
      color: '#D4B896', // Light tan
    },
    {
      title: 'Budget Tracker',
      description: 'Set and track your gift budget',
      icon: <CardGiftcardIcon sx={{ fontSize: 60 }} />,
      path: '/budget-tracker',
      color: '#C99A7A', // Soft terracotta
    },
    {
      title: 'Calendar',
      description: 'View all your celebrations in a calendar',
      icon: <EventIcon sx={{ fontSize: 60 }} />,
      path: '/calendar',
      color: '#7A6B5A', // Neutral brown
    },
    {
      title: 'Family Feed',
      description: 'View family announcements, photos, and important updates',
      icon: <FeedIcon sx={{ fontSize: 60 }} />,
      path: currentFamily ? `/families/${currentFamily.id}/feed` : '/families',
      color: '#8B9A8C', // Muted green for variety
    },
  ];

  const features = getFeatures();

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, mb: 4, px: { xs: 1, sm: 2 } }}>
      <Box
        sx={{
          mb: 4,
          bgcolor: 'background.paper',
          borderRadius: 3,
          p: { xs: 2, sm: 3 },
          textAlign: 'center',
          boxShadow: '0 2px 12px rgba(255, 255, 255, 0.6)',
        }}
      >
        <Typography variant="h3" component="h1" gutterBottom>
          Welcome to Jubilea!
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Manage your celebrations all in one place
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      ) : families.length === 0 ? (
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Card sx={{ p: 3, bgcolor: 'rgba(255, 255, 255, 0.8)', color: '#5A5047' }}>
            <Typography variant="h6" gutterBottom>
              Get Started
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Create or join a family to start managing your celebrations!
            </Typography>
            <Button
              variant="contained"
              sx={{ bgcolor: '#D4A574', '&:hover': { bgcolor: '#B8956A' } }}
              onClick={() => navigate('/families')}
            >
              Create or Join Family
            </Button>
          </Card>
        </Box>
      ) : !currentFamily && families.length > 0 ? (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            Select a Family
          </Typography>
          <Grid container spacing={3}>
            {families.map((family) => {
              const isOwner = family.owner_id === user?.uid;
              const userMember = family.members?.find(m => m.user_id === user?.uid);

              return (
                <Grid item xs={12} sm={6} md={4} key={family.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6,
                      },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                        <Typography variant="h6" gutterBottom>
                          {family.name}
                        </Typography>
                        {isOwner && (
                          <Chip label="Owner" size="small" />
                        )}
                        {userMember && !isOwner && (
                          <Chip
                            label={userMember.role}
                            size="small"
                          />
                        )}
                      </Box>
                      {family.description && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {family.description}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        <GroupIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                        {family.member_count} members
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          selectFamily(family);
                          navigate('/');
                        }}
                        fullWidth
                      >
                        Select Family
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/families')}
            >
              Manage Families
            </Button>
          </Box>
        </Box>
      ) : null}

      {currentFamily && (
        <Box
          sx={{
            mb: 2,
            textAlign: 'center',
            bgcolor: 'background.paper',
            borderRadius: 3,
            p: { xs: 1, sm: 1.5 },
            boxShadow: '0 2px 12px rgba(255, 255, 255, 0.6)',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Currently viewing: <strong>{currentFamily.name}</strong>
          </Typography>
        </Box>
      )}

      {/* Carousel - only show on home page */}
      <NewsFeedCarousel />

      <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
        {features.map((feature) => (
          <Grid item xs={12} sm={6} md={4} key={feature.title}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: { xs: 2, sm: 3 } }}>
                <Box sx={{ color: iconColor || feature.color, mb: 2 }}>
                  <Box sx={{ fontSize: { xs: 48, sm: 60 } }}>
                    {feature.icon}
                  </Box>
                </Box>
                <Typography variant="h5" component="h2" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '0.9rem' } }}>
                  {feature.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: { xs: 1.5, sm: 2 }, px: { xs: 1, sm: 2 } }}>
                <Button
                  size="large"
                  variant="contained"
                  onClick={() => navigate(feature.path)}
                  fullWidth={{ xs: true, sm: false }}
                >
                  Get Started
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default Home;
